<template>
  <header
    :class="[
      'courses-header backdrop-blur-sm flex items-center justify-between border-b',
      isDark ? 'bg-[#2B2926]/90 border-[#37332F]' : 'bg-[#F6F5F1]/90 border-brown-100',
    ]"
  >
    <div class="courses-header-group shrink-0">
      <BackButton
        v-if="showBack"
        compact
        disableSafeArea
        :variant="isDark ? 'on-dark' : 'default'"
        :custom-back="customBack"
        class="shrink-0"
      />
      <button
        class="nav-icon-btn"
        :class="isDark ? 'dark-btn' : ''"
        type="button"
        aria-label="Open menu"
        @click="openMenu"
      >
        <ion-icon :icon="menuOutline" />
      </button>
    </div>
    <div class="courses-header-group shrink-0">
      <!-- Which guide the reader is in. Docked to the head of the right-hand
           control group, immediately left of Aa — where the design puts it. It
           used to sit between the two groups, which never read as centred: the
           right group carries up to four buttons against the left group's two,
           so a justify-between middle child lands left of the true centre.
           It takes this group's 8px gap to Aa rather than the design's 10px:
           every other gap in this bar is 8 (ONE-8234 / Figma), and one control
           spaced differently from its neighbours shows more than 2px does. -->
      <ViewTag v-if="viewTag" :view="viewTag" />
      <button
        v-if="textSizeEnabled"
        ref="textSizeBtn"
        class="nav-icon-btn"
        :class="isDark ? 'dark-btn' : ''"
        type="button"
        aria-label="Text size"
        :aria-expanded="textSizeOpen"
        data-testid="courses-text-size"
        @click="textSizeOpen = !textSizeOpen"
      >
        <span class="nav-aa" aria-hidden="true">Aa</span>
      </button>
      <button
        v-if="showThemeToggle"
        class="nav-icon-btn"
        :class="isDark ? 'dark-btn' : ''"
        type="button"
        :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        @click="$emit('toggle-theme')"
      >
        <ion-icon :icon="isDark ? sunnyOutline : moonOutline" />
      </button>
      <button
        v-if="showAddNote"
        class="nav-icon-btn"
        :class="isDark ? 'dark-btn' : ''"
        type="button"
        aria-label="New note"
        @click="$emit('new-note')"
      >
        <ion-icon :icon="addOutline" />
      </button>
      <button
        v-if="canUseNoteFeatures && notesFeaturesAvailable"
        class="nav-icon-btn"
        :class="isDark ? 'dark-btn' : ''"
        type="button"
        aria-label="Notes and highlights"
        @click="openNotesHub"
      >
        <ion-icon :icon="readerOutline" />
      </button>
    </div>

    <!-- Anchored to the header rather than teleported: it belongs to this bar,
         and the reader below it stays visible so the size change can be judged
         against the actual lesson text while choosing. -->
    <!-- Teleported to ion-app, per docs/ION_MODAL_PATTERN.md. An absolutely
         positioned panel inside the header is at the mercy of the stacking and
         containing blocks Ionic puts around a page; teleporting puts it above
         all of them. Position is fixed rather than anchored for the same reason
         — there is no shared coordinate space to anchor to once it moves out. -->
    <Teleport defer to="ion-app">
      <template v-if="textSizeOpen">
        <!-- Backdrop catches the dismiss tap. A document-level listener would
             also have to dodge the toggle button that opened the panel. -->
        <div class="text-size-scrim" @click="textSizeOpen = false" />
        <!-- courses-theme, not a hand-picked subset: teleporting leaves the
             page's theme scope behind, and redefining only surface and border
             left --courses-ink at its light value — dark ink on a dark panel.
             Re-applying the real scope brings every token, in step with the
             page it was opened from. -->
        <div class="courses-theme text-size-pop" :class="{ 'courses-dark': isDark }">
          <FontScaleControl />
        </div>
      </template>
    </Teleport>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';
import { IonIcon } from '@ionic/vue';
import FontScaleControl from '@/components/common/FontScaleControl.vue';
import { isFontScaleEnabled } from '@/composables/useFontScale';
import { menuOutline, addOutline, readerOutline, moonOutline, sunnyOutline } from 'ionicons/icons';
import BackButton from '@/components/common/BackButton.vue';
import ViewTag from './ViewTag.vue';
import { openCoursesMenu } from '@/utils/courses-menu';
import { coursesNotesAvailable } from '@/utils/courses-notes-availability';
import directedNavigation from '@/mixins/DirectedNavigation';

export default {
  mixins: [directedNavigation],
  name: 'CoursesHeader',
  components: {
    ViewTag,
    FontScaleControl,
    IonIcon,
    BackButton,
  },
  props: {
    showBack: {
      type: Boolean,
      default: true,
    },
    menuId: {
      type: String,
      default: 'courses-menu',
    },
    contentId: {
      type: String,
      default: '',
    },
    customBack: {
      type: Function,
      default: null,
    },
    // Dark theme styling — opt-in. The homepage drives this; the lesson reader
    // leaves it false (light mode).
    isDark: {
      type: Boolean,
      default: false,
    },
    // Only the homepage surfaces the light/dark theme toggle button.
    showThemeToggle: {
      type: Boolean,
      default: false,
    },
    // Opt-in "New Note" (+) button. The lesson reader turns this on; the program
    // list and other consumers leave it off so the button doesn't appear there.
    showAddNote: {
      type: Boolean,
      default: false,
    },
    // 'student' | 'leader' — shows the view tag (ONE-8241). Omitted on screens
    // where the distinction does not apply (the program list, a story page).
    viewTag: {
      type: String,
      default: '',
      validator: value => value === '' || value === 'student' || value === 'leader',
    },
  },
  emits: ['toggle-theme', 'new-note'],
  computed: {
    // Note-hub entry is gated to members who can use note features (FULL tier or
    // leader). Hidden until the access summary loads. See courses store.
    ...mapGetters('courses', ['canUseNoteFeatures']),

    // The other half of the gate: whether this BUILD offers notes at all. Native
    // always; web only under the test-only `courses-notes-web` flag (encryption
    // spec, decision 7). Resolved in a computed for the same reason as
    // textSizeEnabled below — the flag lands after boot.
    notesFeaturesAvailable() {
      return coursesNotesAvailable();
    },

    // Computed, not data: flags arrive over the network after boot, so a cold
    // deep link into a lesson can evaluate this before the answer lands. Read
    // once, an enabled member would see no control until Ionic destroyed and
    // rebuilt this cached page.
    textSizeEnabled() {
      return isFontScaleEnabled();
    },
  },
  data() {
    return {
      textSizeOpen: false,
      menuOutline,
      addOutline,
      readerOutline,
      moonOutline,
      sunnyOutline,
    };
  },
  methods: {
    async openMenu() {
      if (!this.contentId) return;
      await openCoursesMenu(this.menuId, this.contentId);
    },
    openNotesHub() {
      // Carry the current part (`programSlug`, present on both the lesson and the
      // program-landing routes) so the hub scopes to it. On a lesson route also
      // carry `lessonId` so the hub pre-scopes its lesson filter to this lesson.
      const lessonSlug = this.$route.params.lessonId;
      const program = this.$route.params.programSlug;
      const query = {};
      if (program) query.program = program;
      if (lessonSlug) query.lesson = lessonSlug;
      // An empty `query` object resolves to plain /courses/notes, so no guard needed.
      //
      // Returns to the hub this lesson was opened FROM (same `?program=`/`?lesson=`
      // scope) instead of stacking a second one — a hub scoped to another lesson
      // is a different screen and still gets pushed. See goToOrReturn in
      // DirectedNavigation for why the round trip must not stack.
      this.goToOrReturn({ path: '/courses/notes', query });
    },
  },
};
</script>

<style scoped>
.nav-aa {
  /* px like the rest of the bar: this glyph labels the text-size control, so in
     rem it grew as the control was dragged. */
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

/* Reserve iOS status-bar / Android notch safe area on the top edge.
   Mirrors Header.vue (max(34px, ...)) and BottomNav.vue's safe-area pattern. */
.courses-header {
  /* Containing block for the text-size popover, which hangs off the bar. */
  position: relative;
  /* 48px bar, 20px side insets (ONE-8234 / Figma). The safe-area inset is padding
     ON TOP of the bar rather than part of it, so the bar itself is 48 whatever the
     device puts above it. */
  min-height: calc(48px + max(0px, env(safe-area-inset-top, 0px)));
  padding-top: max(0px, env(safe-area-inset-top, 0px));
  padding-left: 20px;
  padding-right: 20px;
  gap: 8px;
}

/* Both control clusters: 8px between buttons, matching the Figma. */
.courses-header-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* An iPad has no notch, so the safe-area inset is just the ~20pt status bar and
   the phone rule leaves the controls sitting almost against it. Add the gap on
   top of the inset rather than max()-ing against it, so the breathing room is
   below the status bar instead of being swallowed by it. */
@media (min-width: 744px) {
  .courses-header {
    /* px, matching the phone rule's fixed 48px bar. In rem the tablet bar grew
       with the reader's text size, so every step of the text-size slider pushed
       the whole page down and back — the drag read as the page strobing rather
       than the type growing. */
    padding-top: calc(env(safe-area-inset-top, 0px) + 24px);
    padding-bottom: 20px;
    min-height: 0;
  }
}

/* A 32px VISIBLE circle inside a 44px tap target — the shared BackButton's own
   proportions (a 32px icon in a 44px button), so every control in this bar is the
   same size as the back button beside it and as the rest of the app.
   ONE-8234 asked for exactly that. It used to be the other way round: the bar
   inflated BackButton's icon to 44px to match these, which made the Next Steps back
   button visibly bigger than every other back button in HOGC One.
   The circle is drawn by ::before rather than on the button, because the button IS
   the tap target and must stay 44 while the paint stays 32. */
.nav-icon-btn {
  @apply relative flex items-center justify-center transition-colors shrink-0;
  width: var(--hit-standard);
  height: var(--hit-standard);
  border: none;
  background: none;
  color: #1d212b;
}

.nav-icon-btn::before {
  content: '';
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid #e2ddd4;
  transition: background-color 0.15s ease-out;
}

.nav-icon-btn:hover::before,
.nav-icon-btn:active::before {
  background-color: rgb(0 0 0 / 6%);
}

/* The icon has to sit above the circle ::before paints. */
.nav-icon-btn > * {
  position: relative;
}
.nav-icon-btn ion-icon {
  font-size: var(--icon-md);
}

/* The dark variant paints the circle, which is ::before now — setting
   border-color on the button itself would style a border that no longer exists. */
.dark-btn {
  color: #ffffff;
}
.dark-btn::before {
  border-color: #6b6460;
}
.dark-btn:hover::before,
.dark-btn:active::before {
  background-color: rgb(255 255 255 / 8%);
}
</style>

<!-- Unscoped: the panel is teleported out of this component's subtree, so a
     scoped attribute selector would never match it. -->
<style>
.text-size-scrim {
  position: fixed;
  inset: 0;
  z-index: 19;
}
.text-size-pop {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 72px);
  right: 12px;
  z-index: 20;
  padding: 14px;
  border: 1px solid var(--courses-border, #e0d9d1);
  border-radius: 16px;
  background: var(--courses-surface-raised, #fff);
  box-shadow: 0 10px 30px rgb(0 0 0 / 18%);
}
</style>
